import React, { useState } from 'react';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Box,
  TextField,
  Snackbar,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
} from '@material-ui/core';
import { DateTime } from 'luxon';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useForm } from 'react-hook-form';
import { Visibility } from '@material-ui/icons';
import { req } from '@omega-energia/react';
import { ComissionPercentage, InstallmentType } from '../../../../../helpers/enums';
import { useAuth } from '../../../../../auth/authProvider';

export default function ModalAdicionarComissao({ isOpen, onClose, refetch, id: partnerInvoiceId }) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      partnerInvoiceId,
      customerName: '',
      installmentAmount: '',
      installmentType: 'CONTRACT_SIGNING',
      referenceDate: DateTime.now().toFormat('yyyy-MM-dd'),
      comissionPercentage: 0.6,
      consumerUnitCode: '',
    },
  });

  const closeAndResetForm = () => {
    reset({
      partnerInvoiceId,
      customerName: '',
      installmentAmount: '',
      installmentType: 'CONTRACT_SIGNING',
      referenceDate: DateTime.now().toFormat('yyyy-MM-dd'),
      comissionPercentage: 0.6,
      consumerUnitCode: '',
    });
    onClose();
  };

  const onSubmit = async data => {
    try {
      data.installmentAmount = UnMaskMoney(data.installmentAmount);
      setIsLoading(true);
      await req.post('/partner-invoice-comissioning-leads', data, { token });
      setSnackbar({ open: true, message: 'Comissão adicionada com sucesso!' });
      setTimeout(() => {
        closeAndResetForm();
        if (refetch) {
          refetch();
        }
      }, 2000);
    } catch (error) {
      const isInternalServerError =
        error?.status === 500 ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('internal server error'));

      setSnackbar({
        open: true,
        message: isInternalServerError
          ? 'Houve um erro ao adicionar a comissão'
          : error?.message || 'Houve um erro ao adicionar a comissão',
      });
    } finally {
      setIsLoading(false);
    }
  };

  function UnMaskMoney(value) {
    if (typeof value === 'number') {
      return value;
    }
    const numericValue = value.replace(/\D/g, '');
    return numericValue ? parseFloat((parseInt(numericValue, 10) / 100).toFixed(2)) : 0;
  }

  function MoneyMask(value) {
    if (typeof value === 'number') {
      return value?.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    }

    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) {
      return '';
    }

    const formatted = (parseInt(numericValue, 10) / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

    return formatted;
  }

  return (
    <>
      <Dialog open={isOpen} maxWidth="md">
        <DialogTitle>
          <Typography>Adicione um valor à comissão</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column" mb={2}>
              <TextField
                variant="outlined"
                label="Código da unidade consumidora"
                name="consumerUnitCode"
                placeholder="Insira o código da unidade consumidora"
                {...register('consumerUnitCode', {
                  required: 'Este campo é obrigatório',
                })}
                error={!!errors.consumerUnitCode}
                helperText={errors.consumerUnitCode?.message?.toString()}
              />
            </Box>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <TextField
                variant="outlined"
                label="Nome do cliente"
                name="customerName"
                placeholder="Nome do cliente"
                {...register('customerName', {
                  required: 'Este campo é obrigatório',
                })}
                error={!!errors.customerName}
                helperText={errors.customerName?.message?.toString()}
              />
              <TextField
                variant="outlined"
                label="Valor Comissão"
                name="installmentAmount"
                placeholder="Digite o valor a ser adicionado"
                {...register('installmentAmount', {
                  required: 'Este campo é obrigatório',
                })}
                onChange={event => {
                  setValue('installmentAmount', MoneyMask(event.target.value));
                }}
                error={!!errors.installmentAmount}
                helperText={errors.installmentAmount?.message?.toString()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <Visibility />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box display="flex" flexDirection="row" mb={2} style={{ gap: '12px' }}>
              <TextField
                select
                variant="outlined"
                label="Tipo de comissão"
                name="installmentType"
                placeholder="Selecione a comissão"
                style={{ flex: 1 }}
                value={getValues('installmentType')}
                onChange={e => {
                  setValue('installmentType', e.target.value, { shouldValidate: true });
                }}
                error={!!errors.installmentType}
                helperText={errors.installmentType?.message?.toString()}
              >
                {Object.entries(InstallmentType).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label="Percentual de comissão"
                name="comissionPercentage"
                placeholder="Selecione o percentual de comissão"
                style={{ flex: 1 }}
                value={getValues('comissionPercentage')}
                onChange={e => {
                  setValue('comissionPercentage', parseFloat(e.target.value), { shouldValidate: true });
                }}
                error={!!errors.comissionPercentage}
                helperText={errors.comissionPercentage?.message?.toString()}
              >
                {Object.entries(ComissionPercentage)
                  .sort(([keyA], [keyB]) => parseFloat(keyA) - parseFloat(keyB))
                  .map(([key, value]) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            <Box display="flex" flexDirection="row" mb={2}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                inputVariant="outlined"
                label="Mês Competência"
                InputLabelProps={{
                  style: { color: 'rgba(0, 0, 0, 0.6)' },
                }}
                format="MMM/yyyy"
                views={['year', 'month']}
                maxDate={DateTime.local().endOf('year')}
                style={{ flex: 1 }}
                value={getValues('referenceDate') ? DateTime.fromISO(getValues('referenceDate')).toJSDate() : null}
                onChange={date => {
                  if (date) {
                    const formattedDate = DateTime.fromISO(date).toFormat('yyyy-MM-dd');
                    setValue('referenceDate', formattedDate, { shouldValidate: true, shouldDirty: true });
                  } else {
                    setValue('referenceDate', '', { shouldValidate: true, shouldDirty: true });
                  }
                }}
                error={!!errors.referenceDate}
                helperText={errors.referenceDate?.message?.toString()}
              />
            </Box>
            <DialogActions>
              <Button type="button" onClick={() => closeAndResetForm()} color="primary" disabled={isLoading}>
                CANCELAR
              </Button>
              <Button type="submit" color="primary" disabled={isLoading || !isDirty} style={{ minWidth: '75px' }}>
                {isLoading ? <CircularProgress size={14} /> : 'SALVAR'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: '' })}
      />
    </>
  );
}
